// Réglages d'une app : origines, visibilité, types, notifications, agent,
// webhooks. Le serveur valide (feedback.AppSettings.Normalize) et refuse
// l'incohérent ; son message est affiché tel quel.
import type { JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { FEEDBACK_TYPES, type AppDetail, type AppSettings, type FeedbackType, type Webhook } from '@feedbaacks/sdk'
import { Button, errorMessage, Spinner, TYPE_META, useFeedbaacks } from '@feedbaacks/ui'
import { d } from '../i18n'

const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean)
const EVENTS = ['item_created', 'item_updated', 'status_changed', 'comment_created', 'vote']

export function Settings({ appKey, onRenamed }: { appKey: string; onRenamed: () => void }): JSX.Element {
  const { client, notify, t } = useFeedbaacks()
  const [app, setApp] = useState<AppDetail | null>(null)
  const [name, setName] = useState('')
  const [origins, setOrigins] = useState('')
  const [s, setS] = useState<AppSettings | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    client.apps.get(appKey).then((a) => {
      setApp(a)
      setName(a.name)
      setOrigins(a.allowed_origins.join('\n'))
      setS(a.settings)
    }, (err: unknown) => notify('error', errorMessage(err, d('error_generic'))))
  }, [client, appKey])

  if (!app || !s) return <div class="dash-center"><Spinner size={24} /></div>
  const set = (patch: Partial<AppSettings>) => setS({ ...s, ...patch })
  const hooks = s.webhooks ?? []
  const setHook = (i: number, patch: Partial<Webhook>) => set({ webhooks: hooks.map((h, j) => (j === i ? { ...h, ...patch } : h)) })

  const save = async () => {
    setBusy(true)
    try {
      const updated = await client.apps.update(appKey, { name: name.trim(), allowed_origins: lines(origins), settings: s })
      setApp(updated)
      setS(updated.settings)
      setOrigins(updated.allowed_origins.join('\n'))
      notify('success', d('saved'))
      if (updated.name !== app.name) onRenamed()
    } catch (err) {
      notify('error', errorMessage(err, d('error_generic')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="dash-page" data-testid="dash-settings" onSubmit={(e) => {
      e.preventDefault()
      void save()
    }}>
      <section class="dash-card">
        <label class="dash-field"><span>{d('settings_name')}</span>
          <input class="fb-input" value={name} onInput={(e) => setName(e.currentTarget.value)} required />
        </label>
        <label class="dash-field"><span>{d('settings_origins')}</span>
          <textarea class="fb-textarea fb-mono" rows={3} value={origins} onInput={(e) => setOrigins(e.currentTarget.value)} data-testid="dash-settings-origins" />
          <small class="fb-faint">{d('settings_origins_hint')}</small>
        </label>
        <fieldset class="dash-field"><legend>{d('settings_visibility')}</legend>
          {(['own', 'all'] as const).map((v) => (
            <label key={v} class="fb-check">
              <input type="radio" name="visibility" checked={s.visibility === v} onChange={() => set({ visibility: v })} data-testid={`dash-visibility-${v}`} />
              <span>{d(v === 'own' ? 'settings_visibility_own' : 'settings_visibility_all')}</span>
            </label>
          ))}
        </fieldset>
        <label class="fb-check"><input type="checkbox" checked={s.allow_anonymous} onChange={(e) => set({ allow_anonymous: e.currentTarget.checked })} />
          <span>{d('settings_anonymous')}</span></label>
        <fieldset class="dash-field"><legend>{d('settings_types')}</legend>
          <div class="fb-chips">
            {FEEDBACK_TYPES.map((type: FeedbackType) => (
              <label key={type} class="fb-check">
                <input type="checkbox" checked={s.types.includes(type)} onChange={(e) => set({ types: e.currentTarget.checked ? [...s.types, type] : s.types.filter((x) => x !== type) })} />
                <span>{t(TYPE_META[type].key)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label class="dash-field"><span>{d('settings_delete_window')}</span>
          <input class="fb-input" type="number" min={1} value={s.author_delete_window_seconds} onInput={(e) => set({ author_delete_window_seconds: Number(e.currentTarget.value) })} />
        </label>
        <label class="dash-field"><span>{d('settings_item_url')}</span>
          <input class="fb-input fb-mono" value={s.item_url} placeholder="https://app.exemple.fr/feedback/{id}" onInput={(e) => set({ item_url: e.currentTarget.value })} />
        </label>
      </section>

      <section class="dash-card">
        <label class="fb-check"><input type="checkbox" checked={s.notifications.email} onChange={(e) => set({ notifications: { ...s.notifications, email: e.currentTarget.checked } })} />
          <span>{d('settings_notifications')}</span></label>
        <label class="dash-field"><span>{d('settings_monitor')}</span>
          <textarea class="fb-textarea fb-mono" rows={2} value={(s.notifications.monitor ?? []).join('\n')} onInput={(e) => set({ notifications: { ...s.notifications, monitor: lines(e.currentTarget.value) } })} />
        </label>
      </section>

      <section class="dash-card">
        <h2 class="fb-section-title">{d('settings_agent')}</h2>
        <label class="dash-field"><span>{d('settings_trusted')}</span>
          <textarea class="fb-textarea fb-mono" rows={2} value={(s.agent.trusted_authors ?? []).join('\n')} onInput={(e) => set({ agent: { ...s.agent, trusted_authors: lines(e.currentTarget.value) } })} />
        </label>
        <label class="dash-field"><span>{d('settings_approvers')}</span>
          <textarea class="fb-textarea fb-mono" rows={2} value={(s.agent.approvers ?? []).join('\n')} onInput={(e) => set({ agent: { ...s.agent, approvers: lines(e.currentTarget.value) } })} />
        </label>
        <label class="dash-field"><span>{d('settings_stale')}</span>
          <input class="fb-input" type="number" min={1} value={s.agent.stale_hours} onInput={(e) => set({ agent: { ...s.agent, stale_hours: Number(e.currentTarget.value) } })} />
        </label>
      </section>

      <section class="dash-card">
        <h2 class="fb-section-title">{d('settings_webhooks')}</h2>
        {hooks.map((h, i) => (
          <div key={i} class="dash-webhook">
            <input class="fb-input fb-mono" aria-label={d('settings_webhook_url')} placeholder={d('settings_webhook_url')} value={h.url} onInput={(e) => setHook(i, { url: e.currentTarget.value })} />
            <input class="fb-input fb-mono" aria-label={d('settings_webhook_secret')} placeholder={d('settings_webhook_secret')} value={h.secret} onInput={(e) => setHook(i, { secret: e.currentTarget.value })} />
            <fieldset class="fb-chips" aria-label={d('settings_webhook_events')}>
              {EVENTS.map((ev) => (
                <label key={ev} class="fb-check fb-mono">
                  <input type="checkbox" checked={h.events?.includes(ev)} onChange={(e) => setHook(i, { events: e.currentTarget.checked ? [...(h.events ?? []), ev] : (h.events ?? []).filter((x) => x !== ev) })} />
                  {ev}
                </label>
              ))}
            </fieldset>
            <Button variant="ghost" onClick={() => set({ webhooks: hooks.filter((_, j) => j !== i) })}>{d('settings_webhook_remove')}</Button>
          </div>
        ))}
        <Button onClick={() => set({ webhooks: [...hooks, { url: '', secret: '', events: [] }] })}>{d('settings_webhook_add')}</Button>
      </section>

      <div class="dash-actions">
        <Button type="submit" variant="primary" disabled={busy} testId="dash-settings-save">{busy && <Spinner size={14} />}{d('save')}</Button>
      </div>
    </form>
  )
}
